import { batchScheduler } from 'batch-promise';
import quickFetch from 'quick-fetch';
import type { CrmObject, CrmObjectsBatchResponse } from './types';
import { createBatchedEarlyRequester } from '../../quickFetch/createBatchedEarlyRequester';
import { promisifyEarlyRequest } from '../../quickFetch/promisifyEarlyRequest';
import { checkFLPGate, readFLPGate } from './auth';
import { convertLegacyObject } from './convertLegacyObject';

export const makeCrmObjectBatchUrl = async (
  objectTypeId: string,
  calls: { objectId: string }[]
) => {
  const params = new URLSearchParams({
    allPropertiesFetchMode: 'latest_version',
    includeAllProperties: 'true',
    includeCurrentUserPermissions: 'true',
    includeObjectVersion: 'false',
    flpViewValidation: readFLPGate() ?? (await checkFLPGate()),
  });

  for (const { objectId } of calls) {
    params.append('id', objectId);
  }

  return `/inbounddb-objects/v1/crm-objects/${objectTypeId}/batch?${params.toString()}`;
};

export const extractCrmObject = ({
  response,
  input: { objectId },
}: {
  response: CrmObjectsBatchResponse;
  input: CrmObjectCall;
}): CrmObject => {
  const result = response[objectId];

  if (result == null) {
    throw new Error(`CrmObject not found: ${objectId}`);
  }

  return convertLegacyObject(result);
};

type CrmObjectCall = { objectTypeId: string; objectId: string };

const scheduler = batchScheduler()
  .read<CrmObjectCall>()
  .identity(({ objectTypeId, objectId }) => `${objectTypeId}:${objectId}`)
  .partition(({ objectTypeId }) => objectTypeId)
  .execute(async (calls, objectTypeId) =>
    promisifyEarlyRequest<CrmObjectsBatchResponse>({
      url: quickFetch.getApiUrl(
        await makeCrmObjectBatchUrl(objectTypeId, Object.values(calls))
      ),
      dataType: 'json',
    })
  )
  .extract(extractCrmObject)
  .immediate();

export const {
  request: earlyCrmObject,
  consume: consumeEarlyCrmObject,
  __TEST_ONLY_reset: __TEST_ONLY_abortCrmObjectLiteEarlyScheduler,
} = createBatchedEarlyRequester(
  'hs-resources:simpleCrmObject-early',
  scheduler
);
